import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import PhoneNumberInput, { isValidPhoneNumber } from './PhoneNumberInput';

const WHO = [
  { icon: 'bi-journal-check', title: 'Accountants and CA firms', body: 'You already keep the books of small manufacturers, processors and traders, and you know what makes their month-end hard.' },
  { icon: 'bi-hdd-network', title: 'Tally and accounting-software partners', body: 'You set businesses up on software today. iBigDo adds purchase, stock, production and sales on the same GST-ready books, and exports to Tally.' },
  { icon: 'bi-laptop', title: 'IT and ERP consultants', body: 'You help businesses choose and run their systems and want a product small businesses can afford and learn quickly.' },
  { icon: 'bi-people', title: 'Anyone who advises these businesses', body: 'Trade bodies, consultants and business advisers who are trusted by owners of factories, mills, packers and trading houses.' }
];

const STEPS = [
  { icon: 'bi-chat-dots', title: 'Introduce', body: 'Show a business how iBigDo would run its purchases, stock, production, sales and GST invoices. A ready demo playbook, discovery questions and walkthroughs do the heavy lifting.' },
  { icon: 'bi-box-seam', title: 'Set up', body: 'Help the new customer put in its items, opening stock and balances, and start billing. A checklist guides each step.' },
  { icon: 'bi-headset', title: 'Look after', body: 'Be the first to answer their questions. You see their support tickets and reply to them before they wait on us, and you can see when each customer renews.' }
];

const GET = [
  { icon: 'bi-folder2-open', title: 'A ready sales kit', body: 'Demo playbook, discovery questions, onboarding checklist, a brochure you can share on WhatsApp, and captioned walkthroughs of real flows.' },
  { icon: 'bi-card-checklist', title: 'Plans and quotes', body: 'Every plan with its limits and what it includes, and a clean customer quote you can print or save as a PDF.' },
  { icon: 'bi-bar-chart-steps', title: 'An honest comparison', body: 'How iBigDo stands against Tally, Busy, Zoho, Marg and Vyapar, including where we are behind, so you never over-promise.' },
  { icon: 'bi-building-check', title: 'Your customers at a glance', body: 'Each customer’s plan, status and renewal date, what they have paid, and the commission you have earned, been paid and are owed.' },
  { icon: 'bi-ticket-detailed', title: 'Support tickets', body: 'Reply to your customers’ requests, add internal notes for our team, and hand over what you cannot answer.' },
  { icon: 'bi-search', title: 'Search and Ask', body: 'Ask a question in plain words and get an answer from iBigDo’s own help, so you are never stuck in front of a customer.' }
];

const FAQ = [
  { q: 'What happens after I apply?', a: 'We read your application and write to you at the email you give. If we go ahead, we create your account manager record, agree the terms with you, and set up your access when your first customer is linked to you.' },
  { q: 'What do I earn?', a: 'A share of what each customer you look after pays for its plan. Each customer carries its own terms: one rate for the first months, a different rate after that, and sometimes a limit. They are agreed with you before you start, and you can see each customer’s terms in your account.' },
  { q: 'Do I need to know ERP software?', a: 'No. You need to know small businesses. The kit teaches you the product, and our team is behind you.' },
  { q: 'Will I be told what iBigDo cannot do?', a: 'Yes, plainly. The guides name what is not there yet, so you do not promise a customer something we do not have.' }
];

const KINDS = [
  ['CA_ACCOUNTANT', 'Chartered accountant or accounting firm'],
  ['TALLY_SOFTWARE_PARTNER', 'Tally or accounting-software partner'],
  ['IT_CONSULTANT', 'IT or ERP consultant'],
  ['OTHER', 'Something else']
];

const CLIENT_RANGES = [['0-10', 'Fewer than 10'], ['11-50', '11 to 50'], ['51-200', '51 to 200'], ['200+', 'More than 200']];

/** Where this visit came from, from the campaign's address (?utm_source=...&utm_campaign=...), kept for the whole visit so it survives scrolling and reloads */
export function campaignSource(search, stored) {
  const q = new URLSearchParams(search || '');
  const parts = ['utm_source', 'utm_medium', 'utm_campaign', 'ref']
    .map((k) => (q.get(k) ? `${k.replace('utm_', '')}=${q.get(k).slice(0, 40)}` : null))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : (stored || '');
}

const EMPTY = { name: '', firm: '', email: '', phone: '', city: '', kind: '', clients: '', message: '', consent: false, website: '' };

function Partners() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [badField, setBadField] = useState('');
  const source = useRef('');
  const formRef = useRef(null);

  useEffect(() => {
    document.title = 'Become an iBigDo account manager';
    let stored = '';
    try { stored = sessionStorage.getItem('ibigdo.partners.source') || ''; } catch (e) { /* a browser that keeps nothing still works */ }
    source.current = campaignSource(window.location.search, stored);
    try { if (source.current) sessionStorage.setItem('ibigdo.partners.source', source.current); } catch (e) { /* as above */ }
  }, []);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    if (badField === k) setBadField('');
  };

  const fail = (field, message) => {
    setError(message);
    setBadField(field);
    const box = document.getElementById(`ap-${field}`);
    if (box) { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); box.focus({ preventScroll: true }); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBadField('');
    if (!isValidPhoneNumber(form.phone || '')) { fail('phone', 'Please enter a valid phone number, with the country selected.'); return; }
    setBusy(true);
    try {
      await axios.post('/api/public/partner-applications', { ...form, source: source.current });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const m = err.response?.data?.message;
      fail(/phone/i.test(m || '') ? 'phone' : /email/i.test(m || '') ? 'email' : '', m || 'We could not send your application. Please try again in a minute.');
    } finally {
      setBusy(false);
    }
  };

  const goApply = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="d-flex flex-column min-vh-100" data-testid="partners-page">
      <PublicHeader />
      <Container className="py-5 flex-grow-1">
        <Row className="justify-content-center text-center mb-5">
          <Col lg={9}>
            <h1 className="mb-3">Bring your clients to iBigDo and earn a share of what they pay</h1>
            <p className="text-muted fs-5 mb-4">
              iBigDo is one system for manufacturing, processing and trading businesses: purchase, stock, production, sales and accounts, with GST built in.
              Become an account manager: introduce it, set it up, look after your customers and earn a share of what they pay.
            </p>
            <div className="d-flex justify-content-center gap-2 flex-wrap">
              <Button variant="primary" size="lg" onClick={goApply} data-testid="partners-apply-top">Apply to become an account manager</Button>
              <Button variant="outline-primary" size="lg" href="#how">See how it works</Button>
            </div>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <h2 className="text-center h4 mb-4">Who it is for</h2>
            <Row xs={1} md={2} className="g-3">
              {WHO.map((w) => (
                <Col key={w.title}>
                  <Card className="h-100"><Card.Body>
                    <div className="d-flex align-items-center mb-2"><i className={`bi ${w.icon} me-2 text-primary`} aria-hidden="true" /><Card.Title className="mb-0 h6">{w.title}</Card.Title></div>
                    <Card.Text className="text-muted small mb-0">{w.body}</Card.Text>
                  </Card.Body></Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5" id="how">
          <Col lg={10}>
            <h2 className="text-center h4 mb-4">What an account manager does</h2>
            <Row xs={1} md={3} className="g-3">
              {STEPS.map((s, i) => (
                <Col key={s.title}>
                  <Card className="h-100"><Card.Body>
                    <div className="d-flex align-items-center mb-2"><i className={`bi ${s.icon} me-2 text-primary`} aria-hidden="true" /><span className="text-muted small">{i + 1}</span><Card.Title className="mb-0 ms-2 h6">{s.title}</Card.Title></div>
                    <Card.Text className="text-muted small mb-0">{s.body}</Card.Text>
                  </Card.Body></Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <h2 className="text-center h4 mb-4">What you get</h2>
            <Row xs={1} md={2} lg={3} className="g-3">
              {GET.map((g) => (
                <Col key={g.title}>
                  <Card className="h-100"><Card.Body>
                    <div className="d-flex align-items-center mb-2"><i className={`bi ${g.icon} me-2 text-primary`} aria-hidden="true" /><Card.Title className="mb-0 h6">{g.title}</Card.Title></div>
                    <Card.Text className="text-muted small mb-0">{g.body}</Card.Text>
                  </Card.Body></Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col lg={8}>
            <h2 className="text-center h4 mb-4">Questions</h2>
            {FAQ.map((f) => (
              <div key={f.q} className="mb-3">
                <h3 className="h6 mb-1">{f.q}</h3>
                <p className="text-muted small mb-0">{f.a}</p>
              </div>
            ))}
          </Col>
        </Row>

        <Row className="justify-content-center" ref={formRef} id="apply">
          <Col md={9} lg={7}>
            <Card><Card.Body className="p-4">
              {done ? (
                <div className="text-center py-3" data-testid="partners-thanks">
                  <i className="bi bi-check-circle text-success" style={{ fontSize: '2.5rem' }} />
                  <h2 className="h4 mt-3">Thank you, we have your application</h2>
                  <p className="text-muted mb-4">We will write to you at {form.email} after we have read it. If you do not see our email, please look in your spam folder.</p>
                  <Link to="/">Back to home</Link>
                </div>
              ) : (
                <>
                  <h2 className="h4 mb-1">Apply to become an account manager</h2>
                  <p className="text-muted small mb-4">It takes about two minutes. Applying does not commit you to anything.</p>
                  {error && <Alert variant="danger" data-testid="partners-error">{error}</Alert>}
                  <Form onSubmit={submit} data-testid="partners-form">
                    <Row className="g-3">
                      <Col md={6}><Form.Group><Form.Label>Your name</Form.Label><Form.Control id="ap-name" value={form.name} onChange={set('name')} maxLength={150} required autoComplete="name" isInvalid={badField === 'name'} /></Form.Group></Col>
                      <Col md={6}><Form.Group><Form.Label>Firm or business (optional)</Form.Label><Form.Control id="ap-firm" value={form.firm} onChange={set('firm')} maxLength={150} autoComplete="organization" /></Form.Group></Col>
                      <Col md={6}><Form.Group><Form.Label>Email</Form.Label><Form.Control id="ap-email" type="email" value={form.email} onChange={set('email')} maxLength={150} required autoComplete="email" isInvalid={badField === 'email'} /></Form.Group></Col>
                      <Col md={6}><Form.Group><Form.Label>Phone</Form.Label><PhoneNumberInput id="ap-phone" name="phone" value={form.phone} onChange={set('phone')} required invalid={badField === 'phone'} /></Form.Group></Col>
                      <Col md={6}><Form.Group><Form.Label>City (optional)</Form.Label><Form.Control id="ap-city" value={form.city} onChange={set('city')} maxLength={100} autoComplete="address-level2" /></Form.Group></Col>
                      <Col md={6}><Form.Group><Form.Label>Which describes you best?</Form.Label>
                        <Form.Select id="ap-kind" value={form.kind} onChange={set('kind')} required>
                          <option value="">Choose one</option>
                          {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </Form.Select></Form.Group></Col>
                      <Col md={12}><Form.Group><Form.Label>How many small businesses do you work with? (optional)</Form.Label>
                        <Form.Select id="ap-clients" value={form.clients} onChange={set('clients')}>
                          <option value="">Choose one</option>
                          {CLIENT_RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </Form.Select></Form.Group></Col>
                      <Col md={12}><Form.Group><Form.Label>Anything you would like us to know (optional)</Form.Label><Form.Control id="ap-message" as="textarea" rows={3} value={form.message} onChange={set('message')} maxLength={1000} /></Form.Group></Col>
                      <div style={{ position: 'absolute', left: '-10000px' }} aria-hidden="true">
                        <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
                      </div>
                      <Col md={12}><Form.Check id="ap-consent" type="checkbox" checked={form.consent} onChange={set('consent')} required
                        label="You may contact me about becoming an iBigDo account manager." /></Col>
                    </Row>
                    <Button type="submit" className="mt-4" size="lg" disabled={busy} data-testid="partners-submit">
                      {busy ? <><Spinner size="sm" animation="border" className="me-2" />Sending</> : 'Send my application'}
                    </Button>
                  </Form>
                </>
              )}
            </Card.Body></Card>
          </Col>
        </Row>
      </Container>
      <PublicFooter />
    </div>
  );
}

export default Partners;
